import { JSONContent } from '@tiptap/react';
import { UserInfo } from 'Components/utils/AuthContext';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import {
  Actor,
  AlertActivityHistoryAssigneeChange,
  AlertActivityHistoryCommentAdded,
  AlertActivityHistoryContextTagsChange,
  AlertActivityHistoryQualityChange,
  AlertActivityHistoryRecord,
  AlertActivityHistoryRecordTypes,
  AlertActivityHistorySlackbotBoomerang,
  AlertActivityHistoryStatusChange,
  AlertStatus,
  CommentFormat,
} from 'Generated/schema';
import { getActorDisplayName } from 'Helpers/utils';
import { transformTagToHumanReadable } from 'Pages/Alerts/utils/contextTags';
import { Article, Box, BoxProps, Card, Flex, Grid, Icon, Text } from 'pounceV2';
import React from 'react';
import { useTheme } from '@mui/material';
import CommentV3 from '../Comment/CommentV3';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
const svgBaseProps: BoxProps = {
  display: 'inline-flex',
  borderRadius: 'circle',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
};

const AlertHistoryEventIcon = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const { palette } = useTheme();
  const { tokens } = palette;
  let backgroundColor: string = tokens.background.neutral.raised[3];
  let icon = null;

  if (event?.type === AlertActivityHistoryRecordTypes.StatusChange) {
    const content = event.content as AlertActivityHistoryStatusChange;
    if (content.newStatus === AlertStatus.Closed) {
      // eslint-disable-next-line prefer-destructuring
      backgroundColor = tokens.background.semantic.error[1];
      icon = <Icon type="close" size="small" data-testid="closed-status-icon" />;
    }
    if (content.newStatus === AlertStatus.Open) {
      backgroundColor = tokens.background.semantic.info.base;
      icon = <Icon type="open" size="medium" data-testid="open-status-icon" />;
    }
    if (content.newStatus === AlertStatus.Resolved) {
      backgroundColor = tokens.background.semantic.success.base;
      icon = <Icon type="check" size="small" data-testid="resolved-status-icon" />;
    }
    if (content.newStatus === AlertStatus.Triaged) {
      backgroundColor = tokens.background.semantic.warning.base;
      icon = <Icon type="in-progress" size="medium" data-testid="in-progress-status-icon" />;
    }
  }

  if (event?.type === AlertActivityHistoryRecordTypes.AssigneeChange) {
    // eslint-disable-next-line prefer-destructuring
    backgroundColor = tokens.background.neutral.raised[4];
    icon = <Icon type="user" size="small" data-testid="assignee-change-icon" />;
  }

  if (event?.type === AlertActivityHistoryRecordTypes.Comment) {
    // eslint-disable-next-line prefer-destructuring
    backgroundColor = tokens.background.neutral.raised[4];
    icon = <Icon type="comment-filled" size="small" data-testid="comment-icon" />;
  }

  if (event?.type === AlertActivityHistoryRecordTypes.QualityChange) {
    const content = event.content as AlertActivityHistoryQualityChange;
    // eslint-disable-next-line prefer-destructuring
    const defaultColor = tokens.background.neutral.raised[3];
    const qualityBgMap: Record<string, string> = {
      USEFUL: tokens.background.semantic.success.base,
      NOISE: tokens.background.semantic.error[2],
    };
    backgroundColor = qualityBgMap[content.newQuality || ''] || defaultColor;
    icon = <Icon type="star" size="small" data-testid="quality-change-icon" />;
  }

  if (event?.type === AlertActivityHistoryRecordTypes.ContextTagsChange) {
    // eslint-disable-next-line prefer-destructuring
    backgroundColor = tokens.background.neutral.raised[4];
    icon = <Icon type="tag" size="small" data-testid="context-tags-change-icon" />;
  }

  return (
    <Box {...svgBaseProps} backgroundColor={backgroundColor}>
      {icon}
    </Box>
  );
};

const DEFAULT_INITIALS = '??';
const getUserInitials = (userInfo?: Pick<UserInfo, 'givenName' | 'familyName' | 'email'>) => {
  if (!userInfo) {
    return DEFAULT_INITIALS;
  }

  if (userInfo.givenName && userInfo.familyName) {
    return `${userInfo.givenName[0]}${userInfo.familyName[0]}`;
  }

  if (userInfo.givenName) {
    return userInfo.givenName.slice(0, 2);
  }

  if (userInfo.familyName) {
    return userInfo.familyName.slice(0, 2);
  }

  return userInfo.email.slice(0, 2);
};

const UserAvatar = ({ userInfo }: { userInfo: Actor }) => {
  const { palette } = useTheme();
  const { tokens } = palette;

  return (
    <Box
      width={18}
      mt="-2px"
      display="inline-flex"
      verticalAlign="middle"
      transition="background-color 0.1s linear"
      borderRadius="circle"
      justifyContent="center"
      alignItems="center"
      fontSize="x-small"
      fontWeight="bold"
      height={18}
      backgroundColor={tokens.background.accent[2].base}
      flexShrink={0}
      sx={{
        '&:hover': {
          backgroundColor: tokens.background.accent[2][2],
        },
      }}
    >
      {userInfo?.__typename === 'User' ? (
        getUserInitials(userInfo).toUpperCase()
      ) : (
        <Icon type="thunder" size="small" />
      )}
    </Box>
  );
};

const useStatusMap = () => {
  const { palette } = useTheme();
  const { tokens } = palette;

  return React.useMemo<Record<AlertStatus, { message: string; color: string }>>(
    () => ({
      [AlertStatus.Closed]: {
        message: 'Invalid',
        color: tokens.content.text.semantic.error.base,
      },
      [AlertStatus.Open]: {
        message: 'Open',
        color: tokens.content.text.semantic.info.base,
      },
      [AlertStatus.Resolved]: {
        message: 'Resolved',
        color: tokens.content.text.semantic.success.base,
      },
      [AlertStatus.Triaged]: {
        message: 'Triaged',
        color: tokens.content.text.semantic.warning.base,
      },
    }),
    [tokens],
  );
};

const useQualityMap = () => {
  const { palette } = useTheme();
  const { tokens } = palette;

  return React.useMemo<Record<string, { message: string; color: string }>>(
    () => ({
      USEFUL: { message: 'Useful', color: tokens.content.text.semantic.success.base },
      NOISE: { message: 'Noise', color: tokens.content.text.semantic.error.base },
    }),
    [tokens],
  );
};

const ActorText = ({ actor }: { actor: Actor }) => {
  const { palette } = useTheme();
  const { tokens } = palette;

  return (
    <Box display="inline">
      <UserAvatar userInfo={actor} />
      <Box ml={1} display="inline" color={tokens.content.text.accent[14][2]}>
        {getActorDisplayName(actor)}
      </Box>
    </Box>
  );
};

const AlertHistoryStatusChangeMessage = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const content = event.content as AlertActivityHistoryStatusChange;
  const statusMap = useStatusMap();
  const statusConfig = statusMap[content.newStatus];

  return (
    <Box display="inline">
      <ActorText actor={event.triggeredBy} />
      <Text as="span" display="inline">
        &nbsp;updated the status to&nbsp;
      </Text>
      <Text display="inline" fontWeight="medium" color={statusConfig.color}>
        {statusConfig.message}
      </Text>
    </Box>
  );
};

type AssignmentType = 'selfAssign' | 'unassign' | 'assign';
const AlertHistoryAssigneeChangeMessage = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const content = event.content as AlertActivityHistoryAssigneeChange;
  const assignmentType: AssignmentType = React.useMemo(() => {
    if (content?.newAssignee?.id === event.triggeredBy.id) {
      return 'selfAssign';
    }
    if (content?.newAssignee === null) {
      return 'unassign';
    }
    return 'assign';
  }, [content?.newAssignee, event.triggeredBy.id]);
  return (
    <Box display="inline">
      <ActorText actor={event.triggeredBy} />
      <Text as="span" display="inline">
        &nbsp;
      </Text>
      {assignmentType === 'assign' && (
        <>
          <Text as="span" display="inline">
            assigned this alert to&nbsp;
          </Text>
          <ActorText actor={content.newAssignee} />
        </>
      )}
      {assignmentType === 'unassign' && (
        <Text as="span" display="inline">
          unassigned the alert
        </Text>
      )}
      {assignmentType === 'selfAssign' && (
        <Text as="span" display="inline">
          self assigned the alert
        </Text>
      )}
    </Box>
  );
};

const AlertHistoryQualityChangeMessage = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const { palette } = useTheme();
  const { tokens } = palette;
  const qualityMap = useQualityMap();
  const content = event.content as AlertActivityHistoryQualityChange;
  const quality = content.newQuality || '';
  const qualityConfig = qualityMap[quality] || {
    message: 'unset',
    color: tokens.content.text.neutral[3],
  };

  return (
    <Box display="inline">
      <ActorText actor={event.triggeredBy} />
      <Text as="span" display="inline">
        &nbsp;updated the quality to&nbsp;
      </Text>
      <Text display="inline" fontWeight="medium" color={qualityConfig.color}>
        {qualityConfig.message}
      </Text>
    </Box>
  );
};

const AlertHistoryContextTagsChangeMessage = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const { palette } = useTheme();
  const { tokens } = palette;
  const content = event.content as AlertActivityHistoryContextTagsChange;
  const tags = content.newContextTags || [];

  if (tags.length === 0) {
    return (
      <Box display="inline">
        <ActorText actor={event.triggeredBy} />
        <Text as="span" display="inline">
          &nbsp;removed all context tags
        </Text>
      </Box>
    );
  }

  const formattedTags = tags.map(tag => transformTagToHumanReadable(tag)).join(', ');

  return (
    <Box display="inline">
      <ActorText actor={event.triggeredBy} />
      <Text as="span" display="inline">
        &nbsp;updated context tags to&nbsp;
      </Text>
      <Text display="inline" fontWeight="medium" color={tokens.content.text.accent[14][2]}>
        {formattedTags}
      </Text>
    </Box>
  );
};

const AlertHistoryComment = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const { comment } = event.content as AlertActivityHistoryCommentAdded;
  // Comments created via public API (think CI/CD automation) will be saved as plain text.  In order to render that, we need to attempt to parse all comments and fallback to plain text!
  // This function will allow comments not from the console to contain HTML and *not* attempt to render it as HTML.  Instead it will display the HTML as plain text by leveraging existing browser HTML escaping
  const escapeHTML = React.useCallback((unsafeText: string) => {
    const div = document.createElement('div');
    div.innerText = unsafeText;
    return div.innerHTML;
  }, []);

  const commentContent = React.useMemo<JSONContent | string>(() => {
    if (comment.format === CommentFormat.JsonSchema) {
      return JSON.parse(comment.body);
    }

    if (comment.format === CommentFormat.Html) {
      return comment.body;
    }

    return escapeHTML(comment.body);
  }, [comment.body, comment.format, escapeHTML]);

  return (
    <Flex flexDirection="column" width="100%">
      <Box display="inline">
        <ActorText actor={event.triggeredBy} />
        <Text display="inline">&nbsp;commented</Text>
      </Box>
      <Card p={6} mt={2}>
        <CommentV3 content={commentContent} />
      </Card>
    </Flex>
  );
};

const parseResponseAction = (responseAction: string): string => {
  if (responseAction.includes(':white_check_mark:')) {
    return '✅ Confirmed';
  }
  if (responseAction.includes(':rotating_light:')) {
    return '🚨 Needs Further Investigation';
  }

  return 'Unknown';
};

const AlertHistorySlackbotBoomerang = ({ event }: { event: AlertActivityHistoryRecord }) => {
  const { triggeredBy, content } = event;

  if (!content) {
    return null;
  }

  const boomerang = (content as AlertActivityHistorySlackbotBoomerang).slackbotBoomerangHistory;

  if (!boomerang) {
    return null;
  }

  if (boomerang.historyType === 'QUESTION_ASKED') {
    return (
      <Flex flexDirection="column" width="100%">
        <Flex>
          <ActorText actor={triggeredBy} />
          {boomerang.recipientName ? (
            <Text>&nbsp;sent a 🪃&nbsp; to {boomerang.recipientName}</Text>
          ) : (
            <Text>&nbsp;sent a 🪃 to someone</Text>
          )}
        </Flex>
        <Card p={6} mt={2}>
          <Box mb={2}>
            <Text fontWeight="bold">Question:</Text>
          </Box>
          <Box pl={1}>
            <Text fontStyle="italic">
              {boomerang.question ? `"${boomerang.question}"` : '(empty)'}
            </Text>
          </Box>
        </Card>
      </Flex>
    );
  }

  if (boomerang.historyType === 'RESPONSE') {
    return (
      <Flex flexDirection="column" width="100%">
        <Flex>
          {boomerang.recipientName || 'Someone'}
          <Text>&nbsp;returned a 🪃&nbsp; from&nbsp;</Text>
          <ActorText actor={triggeredBy} />
        </Flex>
        <Card p={6} mt={2}>
          <Box mb={2}>
            <Text fontWeight="bold">Question:</Text>
          </Box>
          <Box mb={4} pl={1}>
            <Text fontStyle="italic">
              {boomerang.question ? `"${boomerang.question}"` : '(empty)'}
            </Text>
          </Box>
          <Flex mb={2}>
            <Text fontWeight="bold" mr={2}>
              Response:
            </Text>{' '}
            {parseResponseAction(boomerang.responseAction)}
          </Flex>
          <Box pl={1}>
            <Text fontStyle="italic">
              {boomerang.responseMessage ? `"${boomerang.responseMessage}"` : '(empty)'}
            </Text>
          </Box>
        </Card>
      </Flex>
    );
  }

  return null;
};

const AlertHistoryEventV3 = ({
  isFinal = false,
  event,
}: {
  isFinal?: boolean;
  event: AlertActivityHistoryRecord;
}) => {
  const { palette } = useTheme();
  const { tokens } = palette;
  const dateTime = `${dayjs(event.createdAt).format('MMMM D, YYYY hh:mm:ss A z')} (${dayjs(
    event.createdAt,
  ).fromNow()})`;

  return (
    <Article aria-label="Activity History Event">
      <Grid templateColumns="auto 1fr" columnGap={3} rowGap="2px" pt={1}>
        <Box>
          <AlertHistoryEventIcon event={event} />
        </Box>
        <Flex
          as="time"
          align="center"
          color={tokens.content.text.neutral[2]}
          fontSize="small-medium"
        >
          {dateTime}
        </Flex>
        {!isFinal && (
          <Box display="flex" alignItems="center" justifyContent="center">
            <Box
              width={2}
              height="100%"
              backgroundColor={tokens.stroke.neutral[4]}
              borderRadius="circle"
            ></Box>
          </Box>
        )}

        <Flex gridColumn="2" pb={isFinal ? 0 : 8} alignItems="center" gap={1}>
          {event.type === AlertActivityHistoryRecordTypes.StatusChange && (
            <AlertHistoryStatusChangeMessage event={event} />
          )}
          {event.type === AlertActivityHistoryRecordTypes.AssigneeChange && (
            <AlertHistoryAssigneeChangeMessage event={event} />
          )}
          {event.type === AlertActivityHistoryRecordTypes.Comment && (
            <AlertHistoryComment event={event} />
          )}
          {event.type === AlertActivityHistoryRecordTypes.SlackbotBoomerang && (
            <AlertHistorySlackbotBoomerang event={event} />
          )}
          {event.type === AlertActivityHistoryRecordTypes.QualityChange && (
            <AlertHistoryQualityChangeMessage event={event} />
          )}
          {event.type === AlertActivityHistoryRecordTypes.ContextTagsChange && (
            <AlertHistoryContextTagsChangeMessage event={event} />
          )}
        </Flex>
      </Grid>
    </Article>
  );
};

export default AlertHistoryEventV3;
